import { Link } from "react-router-dom";
import { Section, FinalCTA } from "@/components/v2/blocks";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import { Card, CardContent } from "@/components/ui/card";
import { Truck, KeyRound, ClipboardCheck, Gauge, FileText, Scale, ReceiptText, Mail, ArrowRight } from "lucide-react";

const ETAPES = [
  { icon: Truck, title: "Avant le départ", text: "Préparation des démarches de résiliation et de transfert." },
  { icon: KeyRound, title: "Avant l'arrivée", text: "Organisation des contrats dans votre nouveau logement." },
  { icon: ClipboardCheck, title: "Après l'installation", text: "Vérification des informations contractuelles." },
];

const OCCASION = [
  { icon: Gauge, title: "Réévaluer ses besoins", text: "Vos besoins énergétiques évoluent avec votre nouveau logement." },
  { icon: FileText, title: "Mieux comprendre son contrat", text: "L'occasion de faire le point sur vos contrats actuels." },
  { icon: Scale, title: "Étudier les solutions", text: "Comparer les offres adaptées à votre nouvelle situation." },
];

const PLUS_LOIN = [
  { to: "/particuliers/comparer-les-offres", label: "Comparer les offres", sub: "Particuliers", icon: Scale },
  { to: "/particuliers/comprendre-sa-facture", label: "Comprendre sa facture", sub: "Particuliers", icon: ReceiptText },
  { to: "/contact", label: "Nous contacter", sub: "Étude gratuite", icon: Mail },
];

function RevealCard({ icon: Icon, title, text }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-500 ease-out hover:-translate-y-3 hover:scale-[1.05] hover:border-primary hover:bg-primary hover:shadow-2xl hover:shadow-primary/40">
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: "radial-gradient(130% 90% at 50% 0%, rgba(196,240,66,0.30), transparent 62%)" }}
      />
      <div className="relative">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm transition-all duration-500 group-hover:scale-110 group-hover:bg-accent group-hover:text-accent-foreground group-hover:shadow-lg group-hover:shadow-accent/40">
          <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
        </div>
        <h3 className="font-display text-base font-bold text-primary mt-5 leading-snug transition-colors duration-500 group-hover:text-white">{title}</h3>
        <div className="grid grid-rows-[0fr] opacity-0 transition-all duration-500 ease-out group-hover:mt-2 group-hover:grid-rows-[1fr] group-hover:opacity-100">
          <p className="overflow-hidden text-sm leading-relaxed text-white/85">{text}</p>
        </div>
      </div>
    </div>
  );
}

export default function Demenagement() {
  return (
    <div>
      <PageHeroPhoto
        cmsKey="demenagement"
        eyebrow="Particuliers · Déménagement"
        title="Préparez sereinement votre déménagement"
        subtitle="Un déménagement implique de nombreuses démarches administratives. Les contrats d'électricité et de gaz font partie des éléments à anticiper afin d'éviter les mauvaises surprises lors de l'installation."
        image="/img/demenagement.png"
        overlay="cinematic"
        flipLogo
        wideCard
        heroAside={
          <div className="flex justify-end -mr-2 lg:-mr-8">
            <div className="relative">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 360" role="img" aria-label="Étapes à anticiper" className="relative h-56 w-auto md:h-72">
                <g transform="translate(0 18)">
                {/* ligne de la timeline */}
                <path d="M70 88 L70 162 M70 198 L70 272" fill="none" stroke="#5C7A2E" strokeWidth="2.2" strokeLinecap="round" />
                {/* pastilles au repos */}
                <circle cx="70" cy="70" r="18" fill="rgba(255,255,255,0.10)" stroke="#5C7A2E" strokeWidth="1.8" />
                <circle cx="70" cy="180" r="18" fill="rgba(255,255,255,0.10)" stroke="#5C7A2E" strokeWidth="1.8" />
                <circle cx="70" cy="290" r="18" fill="rgba(255,255,255,0.10)" stroke="#5C7A2E" strokeWidth="1.8" />
                {/* numéros des étapes */}
                <text x="106" y="70" dominantBaseline="central" fill="rgba(255,255,255,0.6)" fontSize="40" fontWeight="800" fontFamily="inherit">1</text>
                <text x="106" y="180" dominantBaseline="central" fill="rgba(255,255,255,0.6)" fontSize="40" fontWeight="800" fontFamily="inherit">2</text>
                <text x="106" y="290" dominantBaseline="central" fill="rgba(255,255,255,0.6)" fontSize="40" fontWeight="800" fontFamily="inherit">3</text>
                {/* pastilles qui passent au vert l'une après l'autre */}
                <g className="em-step em-step--1">
                  <circle cx="70" cy="70" r="18" fill="#C4F042" stroke="#5C7A2E" strokeWidth="1.8" />
                  <path d="M61 70 l6.5 6.5 L82 62" fill="none" stroke="#123B2A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <g className="em-step em-step--2">
                  <circle cx="70" cy="180" r="18" fill="#C4F042" stroke="#5C7A2E" strokeWidth="1.8" />
                  <path d="M61 180 l6.5 6.5 L82 172" fill="none" stroke="#123B2A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <g className="em-step em-step--3">
                  <circle cx="70" cy="290" r="18" fill="#C4F042" stroke="#5C7A2E" strokeWidth="1.8" />
                  <path d="M61 290 l6.5 6.5 L82 282" fill="none" stroke="#123B2A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                </g>
              </svg>
            </div>
          </div>
        }
        highlightsLabel="Les étapes à anticiper"
        highlights={[
          "Avant le départ : préparer les démarches",
          "Avant l'arrivée : organiser les contrats",
          "Après l'installation : vérifier les informations",
          "L'occasion de revoir votre situation énergétique",
        ]}
      />

      {/* ÉTAPES — cartes numérotées */}
      <Section cmsKey="demenagement.etapes" eyebrow="Les étapes à anticiper" title="Avant, pendant, après" intro="Trois temps clés pour aborder votre déménagement l'esprit tranquille.">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ETAPES.map(({ icon: Icon, title, text }, i) => (
            <Card key={title} className="relative overflow-hidden rounded-2xl border-border bg-card em-card-hover">
              <CardContent className="p-6">
                <span className="pointer-events-none absolute right-3 top-1 font-display text-6xl font-black text-primary/[0.06]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
                </div>
                <h3 className="relative font-display text-base font-bold text-primary mt-5 leading-snug">{title}</h3>
                <p className="relative text-muted-foreground mt-2 text-sm leading-relaxed">{text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* OCCASION — cartes à révélation */}
      <Section cmsKey="demenagement.occasion" eyebrow="L'occasion de faire le point" title="Un changement de logement, un nouveau départ" intro="Un déménagement est souvent l'occasion idéale de revoir sa situation énergétique." muted>
        <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {OCCASION.map((c) => <RevealCard key={c.title} {...c} />)}
        </div>
      </Section>

      {/* MAILLAGE */}
      <Section title="Pour aller plus loin">
        <div className="grid gap-4 sm:grid-cols-3">
          {PLUS_LOIN.map(({ to, label, sub, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:shadow-md"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-semibold leading-snug text-primary">{label}</span>
                <span className="block text-xs text-muted-foreground">{sub}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
            </Link>
          ))}
        </div>
      </Section>

      <FinalCTA title="Anticipez votre emménagement" text="Demandez une étude avant votre installation pour éviter les mauvaises surprises." />
    </div>
  );
}
